import React, { useState, useEffect, useCallback } from 'react';
import { apiCall } from '../utils/api';

export default function CommunityMessagesBar() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadRecentMessages = useCallback(async () => {
    try {
      setLoading(true);

      // Get all exports
      const exportsData = await apiCall('/api/community/exports');
      if (exportsData.length === 0) {
        setMessages([]);
        return;
      }

      const fifteenDaysAgo = new Date();
      fifteenDaysAgo.setDate(fifteenDaysAgo.getDate() - 15);

      // Fetch messages from the most recent export (limited to first 100)
      const latestExport = exportsData[0];
      const msgs = await apiCall(
        `/api/community/messages?export_id=${latestExport.id}&limit=100&offset=0`
      );

      // Filter to last 15 days
      const recentMsgs = msgs.filter((m) => new Date(m.message_time) >= fifteenDaysAgo);

      // Sort by most recent first
      recentMsgs.sort((a, b) => new Date(b.message_time) - new Date(a.message_time));
      setMessages(recentMsgs);
    } catch (err) {
      setError(err.message);
      console.error('Error loading community messages:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => {
    loadRecentMessages();
  }, [loadRecentMessages]);

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();

    if (date.toDateString() === today.toDateString()) {
      return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    }

    return date.toLocaleDateString('en-GB', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex flex-col h-64 sm:h-96 bg-surface-container rounded-lg border border-outline-variant p-3 sm:p-4">
        <p className="font-bold text-sm mb-2 text-on-surface">💬 Community Chat</p>
        <div className="text-center text-sm text-on-surface py-4">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col h-64 sm:h-96 bg-surface-container rounded-lg border border-outline-variant p-3 sm:p-4">
        <p className="font-bold text-sm mb-2 text-on-surface">💬 Community Chat</p>
        <div className="text-error text-xs py-2">{error}</div>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="flex flex-col h-64 sm:h-96 bg-surface-container rounded-lg border border-outline-variant p-3 sm:p-4">
        <p className="font-bold text-sm mb-2 text-on-surface">💬 Community Chat</p>
        <p className="text-xs text-on-surface">Last 15 days</p>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center text-xs text-on-surface">No recent messages</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-64 sm:h-96 bg-surface-container rounded-lg border border-outline-variant overflow-hidden">
      <div className="px-3 py-2 border-b border-outline-variant flex-shrink-0">
        <p className="font-bold text-sm text-on-surface">💬 Community Chat</p>
        <p className="text-xs text-on-surface">Last 15 days</p>
      </div>

      <div className="flex-1 overflow-y-auto space-y-2 px-2 py-2">
        {messages.map((msg) => (
          <div
            key={msg.id}
            className="text-xs bg-surface-container-low rounded p-2 hover:bg-surface-container-high transition-colors"
          >
            <p className="font-semibold truncate text-on-surface">{msg.author_name}</p>
            <p className="text-on-surface line-clamp-2 mt-1 break-words">{msg.message_text}</p>
            <div className="flex justify-between items-center mt-1">
              <p className="text-on-surface text-xs">{formatTime(msg.message_time)}</p>
              {msg.attachment_count > 0 && (
                <span className="text-xs text-primary">📎 {msg.attachment_count}</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
